import React, { useState } from 'react';
import { dir } from 'i18next';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import { LANGUAGE_I18N_MAP_WITH_LANG_ENUM } from '@/components/LanguageMenu/consts';
import useNavigation from '@/hooks/useNavigation';
import Design from '@/modules/corporate/components/Design';
import { useDesignContext } from '@/modules/corporate/components/Design/context/Design';
import { DesignCard } from '@/modules/corporate/components/Design/enums';
import SidebarTabs from '@/modules/corporate/components/Sidebar/Tabs';
import { useEmbeddedQuizEditor } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/QuizEditorModal/context';
import { TAB_QUERY_KEY } from '@/modules/corporate/modules/quiz/views/QuizEditor';
import { useQuizEditorContext } from '@/modules/corporate/modules/quiz/views/QuizEditor/context/QuizEditor';
import routes from '@/pages/corporate/routes';

import TabDetails from './components/TabDetails';
import { sidebarStyles } from './styles';
import { QuizEditorSidebarProps, QuizEditorSidebarTabs } from './types';

export const QUIZ_SIDEBAR_TABS = (t: TFunction) => [
  {
    label: t('QuizCreator:Question'),
    value: QuizEditorSidebarTabs.Question,
  },
  {
    label: t('QuizCreator:Design.Design'),
    value: QuizEditorSidebarTabs.Design,
  },
  {
    label: t('Common:Actions.Settings'),
    value: QuizEditorSidebarTabs.Settings,
  },
];

function QuizEditorSidebar(props: QuizEditorSidebarProps) {
  const { tab } = props;
  const { quiz } = useQuizEditorContext();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const embeddedEditor = useEmbeddedQuizEditor();
  const [embeddedTab, setEmbeddedTab] = useState(QuizEditorSidebarTabs.Question);

  const { overlayContent } = useDesignContext();

  const activeTab = embeddedEditor
    ? embeddedTab
    : ((tab ?? QuizEditorSidebarTabs.Question) as QuizEditorSidebarTabs);

  const onClickTab = React.useCallback(
    (nextTab: any) => {
      if (embeddedEditor) {
        setEmbeddedTab(nextTab ?? QuizEditorSidebarTabs.Question);
        return;
      }
      navigation.replace(routes.QuizEditor(quiz.id), {
        query: nextTab ? { [TAB_QUERY_KEY]: nextTab } : {},
      });
    },
    [embeddedEditor, navigation, quiz.id],
  );

  return (
    <Stack
      className={cn('flex-shrink-0 border-divider border-e', sidebarStyles)}
      dir={quiz.languageType ? dir(LANGUAGE_I18N_MAP_WITH_LANG_ENUM[quiz.languageType]) : undefined}
      direction="col"
      lang={quiz.languageType ? LANGUAGE_I18N_MAP_WITH_LANG_ENUM[quiz.languageType] : undefined}>
      {overlayContent.type === DesignCard.None ? (
        <Stack className="z-0 h-full w-full" direction="col">
          <SidebarTabs tabs={QUIZ_SIDEBAR_TABS(t)} value={activeTab} onChange={onClickTab} />
          <TabDetails activeTab={activeTab} />
        </Stack>
      ) : (
        <Design.SidebarOverlay />
      )}
    </Stack>
  );
}

export default React.memo(QuizEditorSidebar);
